import moment from "moment-timezone";
import { EmaacAlertHistoryItem } from "app/models/emaac-alert-history.model";
import { IOption } from "app/components/multiselect-dropdown/multiselect-dropdown.component";
import { isValidTime, normalizeTime } from "app/directives/time-input.directive";

/**
 * Alert as a table row. handledByName is the first EMAAC operator, or the "no one logged in" text,
 * so that operators can filter the alerts they handled.
 */
export type EmaacHistoryRow = EmaacAlertHistoryItem & { handledByName: string; expanded?: boolean };

/** Columns that are filtered by picking values present in the data */
export type EmaacHistoryValueColumn = "customer" | "area" | "device" | "handledBy";

/** Value of each filterable column, as shown in the table */
export const EMAAC_HISTORY_COLUMN_VALUE: Record<EmaacHistoryValueColumn, (alert: EmaacHistoryRow) => string> = {
  customer: (alert) => alert.customerName || "",
  area: (alert) => alert.locationName || "",
  device: (alert) => alert.deviceName || alert.deviceId || "",
  handledBy: (alert) => alert.handledByName || "",
};

/**
 * Filters of the EMAAC alert history. Empty value lists and missing times mean "no filter".
 */
export class EmaacHistoryFilters {
  values: Record<EmaacHistoryValueColumn, string[]> = { customer: [], area: [], device: [], handledBy: [] };
  /** Start of the time range, or null */
  from: moment.Moment | null = null;
  /** End of the time range, or null */
  to: moment.Moment | null = null;

  get active(): boolean {
    return !!this.from || !!this.to || Object.values(this.values).some((v) => v.length > 0);
  }
}

/** Options for a column's dropdown: the distinct values in the data, sorted */
export function getColumnOptions(alerts: EmaacHistoryRow[], column: EmaacHistoryValueColumn): IOption[] {
  const values = new Set(alerts.map(EMAAC_HISTORY_COLUMN_VALUE[column]).filter((v) => v !== ""));
  return [...values]
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ value, name: value }));
}

/**
 * Alerts matching all filters. The time range shows alerts that were open at some point within it:
 * started before the range ends and ended after it starts.
 */
export function filterAlerts<T extends EmaacHistoryRow>(alerts: T[], filters: EmaacHistoryFilters): T[] {
  const columns = Object.keys(filters.values) as EmaacHistoryValueColumn[];

  return alerts.filter((alert) => {
    for (const column of columns) {
      const selected = filters.values[column];
      if (selected.length > 0 && !selected.includes(EMAAC_HISTORY_COLUMN_VALUE[column](alert))) {
        return false;
      }
    }

    const start = alert.receiveTime ? moment(alert.receiveTime) : null;
    const end = alert.completeTime ? moment(alert.completeTime) : start;
    if (filters.to && start && start.isAfter(filters.to)) {
      return false;
    }
    if (filters.from && end && end.isBefore(filters.from)) {
      return false;
    }
    return true;
  });
}

/**
 * Combine a date picker value and a time ("15:30", also partly typed like "1530" or "9") in the user's time zone (moment default zone),
 * the same way as the activity log filters. Returns null without a date or with an invalid time.
 */
export function combineDateAndTime(date: Date | null, time: string, defaultTime: string): moment.Moment | null {
  if (!date) {
    return null;
  }
  const hhmm = normalizeTime(time) || defaultTime;
  if (!isValidTime(hhmm)) {
    return null;
  }
  // The picker gives the browser's local midnight: take the calendar day from it, not a converted time
  const day = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  // Parsed in moment's default zone, which the Portal sets to the user's time zone
  return moment(`${day} ${hhmm}`, "YYYY-MM-DD HH:mm");
}
